import { fetch } from 'owa-fetch';
import {
    ActiveCacheHeader,
    CacheAgeHeader,
    CacheHostHeader,
    CacheResultHeader,
    CacheVersionHeader,
    ProxyTypeHeader,
    VersionHeader,
} from 'owa-serviceworker-common/lib/headers';
import { getItem, setItem } from 'owa-local-storage';

import type { BootType } from './types/BootType';
import { getResourcePath } from './bootstrapOptions';
import { isPwa } from './isPwa';

const LocaleStorageKey = 'BootTypeScriptVer';

let calculateError: Error;

export async function calculateBootType(startTime: number): Promise<BootType> {
    if (isPwa()) {
        return 'Pwa';
    }
    try {
        const fetchUrl = `${getResourcePath()}boot-analytics-ping.js`;
        const response = await fetch(fetchUrl);
        // await before returning to catch any errors
        return await parseResponse(startTime, response, fetchUrl);
    } catch (e) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2322: Type 'unknown' is not assignable to type 'Error'.
        // @ts-expect-error
        calculateError = e;
        return 'Unknown';
    }
}

// this is a temporary function to get more information on why IE is not calculating the boot type correctly.
export function getCalculateBootTypeError(): Error {
    return calculateError;
}

let swVersion: string | undefined;
let cdnProvider = 'none';
let cacheResult: string | undefined;
let cacheAge: string | undefined;
let cacheVersion: string | undefined;
let cacheHost: string | undefined;

export interface BootMetadata {
    swVersion: string | undefined;
    cdnProvider: string;
    cacheResult: string | undefined;
    cacheAge: string | undefined;
    cacheVersion: string | undefined;
    cacheHost: string | undefined;
}

export function getBootMetadata(): BootMetadata {
    return {
        swVersion,
        cdnProvider,
        cacheResult,
        cacheAge,
        cacheVersion,
        cacheHost,
    };
}

async function parseResponse(startTime: number, response: Response, fetchUrl: string) {
    const headers = response.headers;

    cdnProvider = headers.get('X-CDN-Provider') || 'none';
    swVersion = headers.get(VersionHeader) || undefined;

    cacheResult = headers.get(CacheResultHeader) || undefined;
    cacheAge = headers.get(CacheAgeHeader) || undefined;
    cacheVersion = headers.get(CacheVersionHeader) || undefined;
    cacheHost = headers.get(CacheHostHeader) || undefined;

    if (headers.get(ActiveCacheHeader)) {
        return 'SwCache';
    }

    const swProxyType = headers.get(ProxyTypeHeader) as BootType | null;
    if (swProxyType) {
        return swProxyType;
    }

    // fetch timings will only be available after the body is read
    await response.text();
    const timing = self.performance?.getEntriesByName?.(fetchUrl)[0] as PerformanceResourceTiming;
    // in CORS mode many properties can be returned as 0
    // so we need to additionally check for decodedBodySize
    if (timing?.transferSize != undefined && timing.decodedBodySize > 0) {
        return timing.transferSize > 0 ? 'NoCache' : 'BrowserCache';
    }

    const responseDateString = headers.get('Date');

    // Some versions of edge and IE don't expose the date header
    // So we are going to store the url that we check against in local storage.
    // Then if it matches the same url that we fetch, we can make a best guess that it
    // was browser cache. This will probably work most of the time because when the
    // browser cache is cleared then local storage should also be cleared
    if (responseDateString == null) {
        const matchesCachedVersion = getItem(self, LocaleStorageKey) == fetchUrl;
        setItem(self, LocaleStorageKey, fetchUrl);
        return matchesCachedVersion ? 'BrowserCache' : 'NoCache';
    }

    const responseDateTimestamp = Date.parse(responseDateString);
    if (isNaN(responseDateTimestamp)) {
        throw new Error('InvalidDate');
    }

    // If the resource date is retrieved after the session start, so this is loaded from online
    // Given that there can be clock difference between server and browser, we allow 1000ms for the delta
    return responseDateTimestamp - startTime > -1000 ? 'NoCache' : 'BrowserCache';
}
